/**
 * Project: DAL (Digital Abstraction Layer)
 * Date: 2026-07-02
 * Description: Partner Test Health dashboard (superadmin). Shows the latest
 *              Bruno run per partner collection, a pass-rate trend per
 *              collection, and a run drill-down with failed requests and any
 *              correlated request latency.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { Activity, AlertTriangle, ArrowLeft, CheckCircle2, RefreshCw, XCircle, AlertCircle } from 'lucide-react'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts'
import { api, type PartnerTestRun, type PartnerTestRunDetail } from '@services/api'
import { toast } from 'react-toastify'

const pct = (n: number): string => `${(n * 100).toFixed(1)}%`
const fmtMs = (ms: number): string => (ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`)
const fmtDate = (iso: string): string => new Date(iso).toLocaleString()

const passRateColor = (rate: number): string => {
  if (rate >= 0.95) return 'text-green-600'
  if (rate >= 0.8) return 'text-amber-500'
  return 'text-red-600'
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { cls: string; icon: React.ReactNode }> = {
    pass: {
      cls: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
      icon: <CheckCircle2 className="h-3.5 w-3.5" />,
    },
    fail: {
      cls: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
      icon: <XCircle className="h-3.5 w-3.5" />,
    },
    error: {
      cls: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
      icon: <AlertCircle className="h-3.5 w-3.5" />,
    },
    skip: {
      cls: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
      icon: <Activity className="h-3.5 w-3.5" />,
    },
  }
  const entry = map[status] ?? map.skip
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${entry.cls}`}>
      {entry.icon}
      {status}
    </span>
  )
}

const PartnerTestHealth: React.FC = () => {
  const [latestRuns, setLatestRuns] = useState<PartnerTestRun[]>([])
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null)
  const [history, setHistory] = useState<PartnerTestRun[]>([])
  const [runDetail, setRunDetail] = useState<PartnerTestRunDetail | null>(null)
  const [loading, setLoading] = useState(false)

  const loadLatest = useCallback(async () => {
    setLoading(true)
    try {
      setLatestRuns(await api.getPartnerTestLatestRuns())
    } catch {
      toast.error('Failed to load partner test runs')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadLatest()
  }, [loadLatest])

  const openCollection = useCallback(async (collectionName: string) => {
    setSelectedCollection(collectionName)
    setRunDetail(null)
    try {
      setHistory(await api.getPartnerTestRunHistory(collectionName, 50))
    } catch {
      toast.error('Failed to load run history')
    }
  }, [])

  const openRun = useCallback(async (runId: number) => {
    try {
      setRunDetail(await api.getPartnerTestRunDetail(runId))
    } catch {
      toast.error('Failed to load run detail')
    }
  }, [])

  // ── Run drill-down view ─────────────────────────────────────────────────
  if (runDetail) {
    const failed = runDetail.results.filter((r) => r.assertion_status === 'fail' || r.assertion_status === 'error')

    return (
      <div className="mx-auto max-w-5xl p-6">
        <button
          onClick={() => setRunDetail(null)}
          className="mb-4 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
        >
          <ArrowLeft className="h-4 w-4" /> Back to history
        </button>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{runDetail.collection_name}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Run #{runDetail.id} · {fmtDate(runDetail.created_at)} · {runDetail.environment ?? 'no env'}
        </p>

        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="text-xs text-gray-500 dark:text-gray-400">Pass rate</div>
            <div className={`text-2xl font-semibold ${passRateColor(runDetail.pass_rate)}`}>
              {pct(runDetail.pass_rate)}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="text-xs text-gray-500 dark:text-gray-400">Passed</div>
            <div className="text-2xl font-semibold text-green-600">{runDetail.passed}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="text-xs text-gray-500 dark:text-gray-400">Failed</div>
            <div className="text-2xl font-semibold text-red-600">{runDetail.failed}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="text-xs text-gray-500 dark:text-gray-400">Errored</div>
            <div className="text-2xl font-semibold text-orange-600">{runDetail.errored}</div>
          </div>
        </div>

        <h2 className="mt-6 mb-2 text-lg font-medium text-gray-900 dark:text-gray-100">
          {failed.length > 0 ? `Failed requests (${failed.length})` : 'All requests passed'}
        </h2>
        <div className="space-y-2">
          {(failed.length > 0 ? failed : runDetail.results).map((r) => (
            <div
              key={r.id}
              className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <StatusBadge status={r.assertion_status} />
                  <span className="font-medium text-gray-800 dark:text-gray-200">{r.request_name}</span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {r.method} {r.status_code ?? '—'} · {fmtMs(r.duration_ms)}
                </div>
              </div>
              {r.error_message && (
                <pre className="mt-2 overflow-x-auto rounded bg-red-50 p-2 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">
                  {r.error_message}
                </pre>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  // ── Collection history view ─────────────────────────────────────────────
  if (selectedCollection) {
    const trend = [...history]
      .reverse()
      .map((run) => ({ date: new Date(run.created_at).toLocaleDateString(), passRate: run.pass_rate * 100 }))
    return (
      <div className="mx-auto max-w-5xl p-6">
        <button
          onClick={() => setSelectedCollection(null)}
          className="mb-4 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
        >
          <ArrowLeft className="h-4 w-4" /> Back to collections
        </button>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{selectedCollection}</h1>

        {trend.length > 0 && (
          <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Pass rate over time</h2>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" fontSize={12} />
                <YAxis domain={[0, 100]} fontSize={12} unit="%" />
                <Tooltip formatter={(v) => `${Number(v).toFixed(1)}%`} />
                <Line type="monotone" dataKey="passRate" stroke="#2563eb" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        <h2 className="mt-6 mb-2 text-lg font-medium text-gray-900 dark:text-gray-100">Run history</h2>
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase dark:bg-white/5 dark:text-gray-400">
              <tr>
                <th className="px-4 py-2">When</th>
                <th className="px-4 py-2">Pass rate</th>
                <th className="px-4 py-2">Passed</th>
                <th className="px-4 py-2">Failed</th>
                <th className="px-4 py-2">Errored</th>
                <th className="px-4 py-2">Duration</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {history.map((run) => (
                <tr
                  key={run.id}
                  onClick={() => void openRun(run.id)}
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900"
                >
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{fmtDate(run.created_at)}</td>
                  <td className={`px-4 py-2 font-medium ${passRateColor(run.pass_rate)}`}>{pct(run.pass_rate)}</td>
                  <td className="px-4 py-2 text-green-600">{run.passed}</td>
                  <td className="px-4 py-2 text-red-600">{run.failed}</td>
                  <td className="px-4 py-2 text-orange-600">{run.errored}</td>
                  <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{fmtMs(run.duration_ms)}</td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-gray-400 dark:text-gray-500">
                    No runs recorded for this collection yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // ── Latest-status overview ──────────────────────────────────────────────
  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Partner Test Health</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Latest Bruno run status per partner collection</p>
        </div>
        <button
          onClick={() => void loadLatest()}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-900"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {latestRuns.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center text-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-500">
          <AlertTriangle className="mx-auto mb-2 h-8 w-8" />
          No partner test runs ingested yet. Run <code>scripts/run-partner-bruno.sh</code> to get started.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {latestRuns.map((run) => (
            <button
              key={run.id}
              onClick={() => void openCollection(run.collection_name)}
              className="dark:hover:bg-blue-900/30/30 rounded-lg border border-gray-200 bg-white p-5 text-left shadow-sm transition-colors hover:border-blue-300 hover:bg-blue-50 dark:border-gray-700 dark:bg-gray-800 dark:shadow-none"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900 dark:text-gray-100">{run.collection_name}</span>
                <span className={`text-xl font-semibold ${passRateColor(run.pass_rate)}`}>{pct(run.pass_rate)}</span>
              </div>
              <div className="mt-3 flex items-center gap-4 text-sm">
                <span className="text-green-600">{run.passed} passed</span>
                <span className="text-red-600">{run.failed} failed</span>
                <span className="text-orange-600">{run.errored} errored</span>
              </div>
              <div className="mt-2 text-xs text-gray-400 dark:text-gray-500">Last run {fmtDate(run.created_at)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default PartnerTestHealth
